import styled, { css } from 'styled-components';
import { TableRow } from '#/components/rfq-common/TableRow';
import createSafeContext from '@paradigm/utils/src/createSafeContext';
import {
  blackTangaroa,
  bombay,
  osloGray,
} from '@paradigm/design-system/src/colors';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import GridCellBase, {
  GridCellProps,
} from '#/components/UnifiedRfqs/components/GridCellBase';

export const ONE_CLICK_ACTION_WIDTH = 718;

const gridAreasWithGreeks =
  '"type product desc action last mybidask  greeks time"';

const gridColumnsWithGreeks = `28px 120px 220px ${ONE_CLICK_ACTION_WIDTH}px 70px 430px 345px 42px`;

const gridAreasMobile = `
"menu type desc"
"menu product desc"
"action action action"
`;
const gridColumnsMobile = `40px calc(50% - 20px) calc(50% - 20px)`;
const gridRowsMobile = `38px 38px 119px`;

export const tableCols = css`
  grid-template-columns: ${gridColumnsWithGreeks};
  grid-template-areas: ${gridAreasWithGreeks};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: grid;
    grid-gap: 0px;
    max-width: 100vw;
    grid-template-columns: ${gridColumnsMobile};
    grid-template-rows: ${gridRowsMobile};
    grid-template-areas: ${gridAreasMobile};
    padding: 0px;
  }
`;

const RfqRowBase = styled(TableRow)`
  ${tableCols}
`;

export const MyOrdersContainer = styled.div.attrs({
  'aria-label': 'my quotes',
})`
  position: relative;
  display: grid;
  width: 100%;
  grid-template-columns: 20px 190px 190px 20px;
  grid-template-areas: 'nbids bid ask nasks';
  gap: 5px;
  height: 100%;
  align-items: center;
  z-index: 0;
`;

const OrderContainer = styled.div<{ noBackground?: boolean }>`
  border-radius: 3px;
  background-color: ${({ noBackground }) =>
    noBackground === true ? 'transparent' : blackTangaroa};
  display: grid;
  align-items: center;
  height: 30px;
  gap: 4px;
`;

export const MyBid = styled(OrderContainer).attrs({ 'aria-label': 'bid' })`
  grid-area: bid;
  grid-template-columns: 80px 8px 90px;
  grid-template-areas: 'qty kill price';
  justify-content: end;
`;

export const MyAsk = styled(OrderContainer).attrs({ 'aria-label': 'ask' })`
  grid-area: ask;
  grid-template-columns: 90px 8px 80px;
  grid-template-areas: 'price kill qty';
  justify-content: start;
`;

export const KillCell = styled.div`
  grid-area: kill;
`;

export const PriceCell = styled.span.attrs({ 'aria-label': 'price' })<{
  disabled: boolean;
}>`
  grid-area: price;
  font-size: 14px;
  ${({ disabled }) => (disabled ? 'opacity:0.5;' : '')}
`;

export const QtyCell = styled.span.attrs({ 'aria-label': 'quantity' })<{
  disabled: boolean;
}>`
  font-weight: bold;
  font-size: 0.8125rem;
  grid-area: qty;
  text-align: center;
  color: ${bombay};
  ${({ disabled }) => (disabled ? 'opacity:0.5;' : '')}
`;

const NumOrders = styled.div`
  background-color: ${blackTangaroa};
  border-radius: 3px;
  font-size: 12px;
  line-height: 12px;
  text-align: center;
  font-weight: 500;
  color: ${osloGray};
  padding: 4px 6.5px;
`;

export const NumBids = styled(NumOrders)`
  grid-area: nbids;
`;

export const NumAsks = styled(NumOrders)`
  grid-area: nasks;
`;

export const RfqType = styled(GridCellBase).attrs<GridCellProps>(() => ({
  area: 'type',
  mobile: true,
}))<GridCellProps>`
  width: 100%;
  font-size: 0.8125rem;
  font-weight: 400;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding-top: 1rem;
    display: flex;
    align-items: end;
  }
`;

export const Product = styled(GridCellBase).attrs<GridCellProps>(() => ({
  area: 'product',
  mobile: true,
}))<GridCellProps>`
  font-size: 0.8125rem;
  font-weight: 400;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding-bottom: 1rem;
  }
`;

export const [useRowIndex, RowIndexProvider] =
  createSafeContext<number>('rowIndexContext');

export default RfqRowBase;
